import React, { useEffect, useState } from "react";
import { Sparkles, Loader2, RefreshCw, ThumbsUp, AlertTriangle, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";

// AI weekly review — reads the athlete's logged week against their targets and
// training, then writes a short review plus three actions.
export default function AiReviewCard({ days = 7, rangeLabel = "7 days" }) {
  const [review, setReview] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/ai/review/latest").then(({ data }) => setReview(data && data.summary ? data : null)).catch(() => {});
  }, []);

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/ai/review?days=${days}`);
      setReview(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not build your review");
    } finally { setBusy(false); }
  };

  return (
    <section className="border border-white/10 bg-[#141414] p-5" data-testid="ai-review-card">
      <div className="flex items-start gap-3">
        <div className="grid place-items-center h-9 w-9 shrink-0 border border-[#A78BFA]/40 bg-[#A78BFA]/10">
          <Sparkles className="h-4 w-4 text-[#A78BFA]" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[10px] uppercase tracking-[0.28em] text-zinc-400">AI review · last {rangeLabel}</p>
          <h3 className="font-display text-lg uppercase tracking-tight leading-tight mt-1" data-testid="ai-review-headline">
            {review ? review.headline : "Get a read on your week"}
          </h3>
        </div>
        <Button data-testid="ai-review-generate-btn" onClick={generate} disabled={busy}
          className="rounded-none shrink-0 bg-[#A78BFA] hover:bg-[#8f6ff5] text-black text-[11px] font-bold uppercase tracking-wider">
          {busy ? <><Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> Reading…</>
                : review ? <><RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Refresh</> : "Generate"}
        </Button>
      </div>

      {!review && !busy && (
        <p className="text-sm text-zinc-300 mt-3">Generate a plain-English review of how your fuelling actually went against your targets and training — with three things to change next week.</p>
      )}

      {review && (
        <div className="mt-4 space-y-4">
          <p className="text-sm text-zinc-100 leading-relaxed" data-testid="ai-review-summary">{review.summary}</p>

          <div className="grid sm:grid-cols-2 gap-3">
            {(review.wins || []).length > 0 && (
              <div className="border border-[#34C759]/25 bg-[#34C759]/[0.05] p-3">
                <p className="text-[10px] uppercase tracking-wider text-[#34C759] font-bold flex items-center gap-1.5 mb-2">
                  <ThumbsUp className="h-3.5 w-3.5" /> Working
                </p>
                <ul className="space-y-1.5">
                  {review.wins.map((w, i) => <li key={i} className="text-[13px] text-zinc-200 leading-snug">{w}</li>)}
                </ul>
              </div>
            )}
            {(review.watch || []).length > 0 && (
              <div className="border border-[#FF9500]/25 bg-[#FF9500]/[0.05] p-3">
                <p className="text-[10px] uppercase tracking-wider text-[#FF9500] font-bold flex items-center gap-1.5 mb-2">
                  <AlertTriangle className="h-3.5 w-3.5" /> Watch
                </p>
                <ul className="space-y-1.5">
                  {review.watch.map((w, i) => <li key={i} className="text-[13px] text-zinc-200 leading-snug">{w}</li>)}
                </ul>
              </div>
            )}
          </div>

          {(review.actions || []).length > 0 && (
            <div data-testid="ai-review-actions">
              <p className="text-[10px] uppercase tracking-[0.28em] text-zinc-400 mb-2">Do this next week</p>
              <ol className="space-y-2">
                {review.actions.map((a, i) => (
                  <li key={i} className="flex gap-3 border-l-2 border-[#E2E30A] pl-3">
                    <ArrowRight className="h-3.5 w-3.5 text-[#E2E30A] mt-1 shrink-0" />
                    <div>
                      <p className="text-sm text-white font-bold">{a.title}</p>
                      <p className="text-[13px] text-zinc-300 leading-snug mt-0.5">{a.detail}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          )}
          <p className="text-[10px] text-zinc-500">Guidance only — not medical advice.</p>
        </div>
      )}
    </section>
  );
}
